import React, { useState } from 'react';
import { X, Loader, Search } from 'lucide-react';
import { SearchableSelect } from '@components/common/SearchableSelect';
import type { AdminUser } from '@schemas/api';

interface AddMemberModalProps {
  isOpen: boolean;
  isLoading: boolean;
  unitName: string;
  availableUsers: AdminUser[];
  onSubmit: (userId: string, membershipType: string) => void;
  onClose: () => void;
}

export const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpen,
  isLoading,
  unitName,
  availableUsers,
  onSubmit,
  onClose,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [membershipType, setMembershipType] = useState<string>('member');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredUsers = availableUsers.filter(
    (user) =>
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = () => {
    if (!selectedUserId) return;
    onSubmit(selectedUserId, membershipType);
    setSelectedUserId('');
    setMembershipType('member');
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-96 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Add Member</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <p className="text-sm text-gray-600">
            Add a user to <span className="font-semibold">{unitName}</span>
          </p>

          {/* User search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Users
            </label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* User list */}
          {availableUsers.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select User
              </label>
              <div className="border border-gray-300 rounded-lg max-h-40 overflow-y-auto">
                {filteredUsers.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="radio"
                      name="user"
                      value={user.id}
                      checked={selectedUserId === user.id}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      disabled={isLoading}
                      className="cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {`${user.first_name} ${user.last_name}`}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </label>
                ))}
              </div>
              {filteredUsers.length === 0 && availableUsers.length > 0 && (
                <p className="text-sm text-gray-500 py-2 text-center">
                  No users match your search
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              No users available
            </p>
          )}

          {/* Membership type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Membership Type
            </label>
            <SearchableSelect
              value={membershipType}
              onChange={setMembershipType}
              disabled={isLoading}
              options={[
                { id: 'member', label: 'Member' },
                { id: 'lead', label: 'Lead' },
                { id: 'manager', label: 'Manager' },
              ]}
              placeholder="Membership type"
              clearable={false}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !selectedUserId}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading && <Loader size={16} className="animate-spin" />}
            Add
          </button>
        </div>
      </div>
    </div>
  );
};
